import React from "react"
import { useFormContext } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"

import Button from "src/components/Button"
import ContactSearch from "src/components/ContactSearch/ContactSearch"
import {
  ADD_NEW_BOAT,
  ADD_NEW_CONTACT,
} from "src/components/ContactSearch/constants"
import { convertBoatDimensionsToInches } from "src/components/ContactSearch/helpers"
import Form from "src/components/Form"

import { createContactBoat } from "src/api/ContactBoat"

const ContactInput = () => {
  const queryClient = useQueryClient()
  const {
    watch,
    setValue,
    setError,
    trigger,
    formState: { errors },
  } = useFormContext()

  const [contact, contactId, contactBoat, contactBoatId] = watch([
    "contact",
    "contact_id",
    "contact_boat",
    "contact_boat_id",
  ])
  const addingNewContact = contactId === ADD_NEW_CONTACT
  const addingNewBoat = contactBoatId === ADD_NEW_BOAT

  const { mutate: saveContactBoatAndContact, isLoading } = useMutation(
    ["addNewContactBoat", contactId],
    createContactBoat,
    {
      onSuccess: ({ boat, contact }) => {
        setValue("contact_boat_id", boat.id, { shouldValidate: true })
        setValue("contact_boat", boat, { shouldValidate: true })
        if (contact) {
          setValue("contact", contact)
          setValue("contact_id", contact.id, { shouldValidate: true })
        }
        queryClient.invalidateQueries(["contactSearch-boats", contactId])
      },
      onError: (error) => {
        setError("root.saveBoatError", {
          message: error.message,
          meta: { invalidEmailUrl: error.link },
        })
      },
    }
  )

  const handleSaveContactAndBoat = async () => {
    if (!(await trigger(["contact_boat", "contact"]))) {
      return
    }
    const {
      name,
      boat_type: boatType,
      make,
      model,
      year,
      hailing_port: hailingPort,
      registration_attributes: registrationAttributes,
      ...boatDimensions
    } = contactBoat

    const params = {
      boat: {
        name,
        boat_type: boatType,
        make,
        model,
        year,
        hailing_port: hailingPort,
        registration_attributes: registrationAttributes,
        ...convertBoatDimensionsToInches(boatDimensions),
      },
      contact,
      contactId:
        contactId && contactId !== ADD_NEW_CONTACT ? contactId : undefined,
    }
    return saveContactBoatAndContact(params)
  }

  const renderRootError = () => {
    if (errors?.root?.saveBoatError && contactBoatId === ADD_NEW_BOAT) {
      const { message, meta } = errors.root.saveBoatError
      if (meta?.invalidEmailUrl) {
        return (
          <Form.Error>
            {message}{" "}
            <a className="text-link" href={meta.invalidEmailUrl}>
              View existing contacts.
            </a>
          </Form.Error>
        )
      } else {
        return <Form.Error>{message}</Form.Error>
      }
    }
  }

  return (
    <div className="flex flex-col space-y-4 pt-2">
      <ContactSearch showCreateNewOutsideForm />
      {renderRootError()}
      {addingNewBoat ? (
        <Button
          variant="primary"
          isLoading={isLoading}
          onClick={handleSaveContactAndBoat}
        >
          {`Save Boat${addingNewContact ? " and Contact" : ""}`}
        </Button>
      ) : null}
    </div>
  )
}

export default ContactInput
